import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const Settings = () => {
    const { isDark } = useTheme();
    const [settings, setSettings] = useState({
        company_name: '',
        address: '',
        phone: '',
        email: '',
        logo_url: null
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    // Mobile check
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchSettings = useCallback(async () => {
        try {
            const response = await fetch('/api/v1/settings', {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                }
            });
            const data = await response.json();
            setSettings({
                company_name: data.company_name || '',
                address: data.address || '',
                phone: data.phone || '',
                email: data.email || '',
                logo_url: data.logo_url
            });
            if (data.logo_url) {
                setLogoPreview(data.logo_url);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setMessage({ type: 'error', text: 'Failed to load settings' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        try {
            const formData = new FormData();
            formData.append('setting[company_name]', settings.company_name);
            formData.append('setting[address]', settings.address);
            formData.append('setting[phone]', settings.phone);
            formData.append('setting[email]', settings.email);
            if (logoFile) {
                formData.append('setting[logo]', logoFile);
            }

            const response = await fetch('/api/v1/settings', {
                method: 'PUT',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({ type: 'success', text: 'Settings updated successfully' });
                setSettings(prev => ({ ...prev, logo_url: data.logo_url }));
            } else {
                setMessage({ type: 'error', text: data.errors ? data.errors.join(', ') : 'Failed to save settings' });
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage({ type: 'error', text: 'An error occurred while saving' });
        } finally {
            setSaving(false);
        }
    };

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        header: {
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
        },
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: isMobile ? '1.25rem' : '2rem',
            margin: isMobile ? '0 1rem' : '0 1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        formGroup: {
            marginBottom: '1.5rem',
        },
        label: {
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: isDark ? '#d1d5db' : '#374151',
        },
        input: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
        },
        textarea: {
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            minHeight: '100px',
            resize: 'vertical',
        },
        logoPreview: {
            width: '150px',
            height: '150px',
            objectFit: 'contain',
            borderRadius: '0.5rem',
            border: `2px dashed ${isDark ? '#374151' : '#d1d5db'}`,
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark ? '#000' : '#f9fafb',
            margin: isMobile ? '0 auto 1rem auto' : '0 0 1rem 0',
        },
        saveButton: {
            padding: '0.75rem 2rem',
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '1rem',
            opacity: saving ? 0.7 : 1,
            pointerEvents: saving ? 'none' : 'auto',
            width: isMobile ? '100%' : 'auto',
        },
        message: (type) => ({
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            background: type === 'success' ? (isDark ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5') : (isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2'),
            color: type === 'success' ? (isDark ? '#34d399' : '#065f46') : (isDark ? '#f87171' : '#991b1b'),
        })
    };

    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#fff' : '#000' }}>Loading settings...</div>;
    }

    return (
        <div style={styles.container}>
            <h1 style={styles.header}>Company Settings</h1>

            <div style={styles.card}>
                {message && (
                    <div style={styles.message(message.type)}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Company Logo</label>
                        <div style={styles.logoPreview}>
                            {logoPreview ? (
                                <img src={logoPreview} alt="Company Logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                            ) : (
                                <span style={{ color: isDark ? '#6b7280' : '#9ca3af' }}>No Logo</span>
                            )}
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            style={{ ...styles.input, padding: '0.5rem' }}
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Company Name</label>
                        <input
                            type="text"
                            name="company_name"
                            value={settings.company_name}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Enter company name"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={settings.email}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="company@example.com"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Phone Number</label>
                        <input
                            type="text"
                            name="phone"
                            value={settings.phone}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="+233..."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Address</label>
                        <textarea
                            name="address"
                            value={settings.address}
                            onChange={handleChange}
                            style={styles.textarea}
                            placeholder="123 Main St, City, Country"
                        />
                    </div>

                    <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
                        <button type="submit" style={styles.saveButton}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Settings;
